import { DEFAULT_OG_IMAGE, SITE_URL } from "../components/SEO";

const privateRobots = "noindex, follow";

const routeSeo = {
  home: {
    title: "ATS Resume Checker & AI Resume Optimizer | JobZam",
    description:
      "Check your ATS resume score, match your CV to real job descriptions, and generate tailored resumes, cover letters and interview preparation with JobZam.",
    path: "/",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "JobZam",
        url: SITE_URL,
        logo: `${SITE_URL}/logo512.png`,
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "JobZam",
        url: SITE_URL,
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "JobZam",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: SITE_URL,
        image: DEFAULT_OG_IMAGE,
        description:
          "JobZam is an AI career platform for ATS resume checking, resume-job description matching, tailored resumes, cover letters, and interview preparation.",
      },
    ],
  },
  about: {
    title: "About JobZam | AI Career Platform for Job Seekers",
    description:
      "Learn how JobZam helps job seekers improve ATS resume scores, tailor applications to job descriptions, and prepare stronger career materials.",
    path: "/about",
  },
  contact: {
    title: "Contact JobZam | Support for Job Seekers",
    description:
      "Contact JobZam for support with resume matching, ATS score reports, cover letters, interview preparation, and account questions.",
    path: "/contact",
  },
  login: {
    title: "Login | JobZam",
    description:
      "Sign in to JobZam to use resume matching, AI resume generation, cover letters, and interview preparation tools.",
    path: "/login",
    robots: privateRobots,
  },
  terms: {
    title: "Terms and Conditions | JobZam",
    description:
      "Read the JobZam terms and conditions for using the AI resume optimizer and career tools.",
    path: "/terms",
  },
  privacy: {
    title: "Privacy Policy | JobZam",
    description:
      "Read how JobZam handles resume, job description, account, and payment data for users of its career tools.",
    path: "/privacy",
  },
  faq: {
    title: "JobZam FAQ | ATS Resume Checker and AI Career Tools",
    description:
      "Find answers about JobZam's ATS resume checker, resume matching, AI resume generation, premium plans, privacy, and refunds.",
    path: "/faq",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is JobZam?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "JobZam is an AI-powered career tool that matches your resume with job descriptions, highlights missing keywords, and generates optimized ATS-friendly resumes.",
          },
        },
        {
          "@type": "Question",
          name: "How does the resume matching work?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Users upload a resume and paste a job description. JobZam analyses both, identifies matched and missing skills, and provides a score with suggestions for improvement.",
          },
        },
        {
          "@type": "Question",
          name: "Is JobZam free to use?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "JobZam lets users generate three optimized resumes for free before choosing a premium plan or discount option.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need to create an account?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Users sign in with Google or email so JobZam can track free usage and keep premium access active.",
          },
        },
        {
          "@type": "Question",
          name: "Is my data safe?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "JobZam processes resume and job details securely and does not sell or share them. The platform uses trusted services such as Supabase and Stripe.",
          },
        },
        {
          "@type": "Question",
          name: "What premium plans do you offer?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "JobZam offers one-time payment options for 30-day premium access and 3-month premium access. Both give users unlimited resume generations.",
          },
        },
      ],
    },
  },
  expertReview: {
    title: "Expert CV Review | Professional ATS CV Feedback | JobZam",
    description:
      "Submit your CV for a professional human review with ATS-focused wording, structure improvements, and role-specific feedback from JobZam.",
    path: "/expert-review",
  },
  zamAgent: {
    title: "Zam Agent | JobZam AI Job Assistant",
    description:
      "Explore Zam Agent, JobZam's AI job assistant for smarter applications, career planning, and future job-search workflows.",
    path: "/zam-agent",
  },
  upload: {
    title: "ATS Resume Checker and Resume Score | JobZam",
    description:
      "Upload your CV, paste a job description, and use JobZam to check your ATS resume score, matched keywords, missing skills, and resume-job fit.",
    path: "/upload",
    robots: privateRobots,
  },
  results: {
    title: "ATS Resume Score Results | JobZam",
    description:
      "Review your JobZam ATS resume score, matched keywords, missing skills, and resume-job description match insights.",
    path: "/results",
    robots: privateRobots,
  },
  generateResume: {
    title: "AI Resume Optimizer and Tailored Resume Generator | JobZam",
    description:
      "Generate an optimized, tailored resume based on your CV, missing keywords, and the job description you want to apply for.",
    path: "/generate-resume",
    robots: privateRobots,
  },
  pricing: {
    title: "JobZam Pricing and Premium Plans",
    description:
      "Compare JobZam free access and premium plans for ATS match scoring, AI resume generation, cover letters, and interview preparation.",
    path: "/pricing",
  },
  success: {
    title: "Payment Successful | JobZam",
    description: "Payment confirmation for JobZam premium access.",
    path: "/success",
    robots: "noindex, nofollow",
  },
  coverLetter: {
    title: "AI Cover Letter Generator | JobZam",
    description:
      "Create a tailored AI cover letter from your optimized resume and the job description, with editable tone and downloadable output.",
    path: "/cover-letter",
    robots: privateRobots,
  },
  interviewPrep: {
    title: "AI Interview Preparation and Role-Specific Questions | JobZam",
    description:
      "Prepare for interviews with role-specific questions, answer guidance, and STAR examples based on your resume and job description.",
    path: "/interview-prep",
    robots: privateRobots,
  },
  jobs: {
    title: "UK Job Search and CV-Matched Jobs | JobZam",
    description:
      "Search UK jobs with CV-based recommendations, skill matching, employer details, and filters including visa sponsorship status where available.",
    path: "/jobs",
    robots: privateRobots,
  },
  notFound: {
    title: "Page Not Found | JobZam",
    description: "The JobZam page you requested could not be found.",
    path: "/404",
    robots: privateRobots,
  },
};

export default routeSeo;
